import { Component, OnInit } from '@angular/core';
import { CreateBoxPickupSitesDialogComponent } from './components/create-box-pickup-sites-dialog/create-box-pickup-sites-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, Observable } from 'rxjs';
import { BoxPickupSite } from '~lmo/models/frac.model';
import { LmoFracsService } from '~lmo/services';
import { MatTableDataSource } from '@angular/material/table';
import { User } from '~models/user';
import { map } from 'rxjs/operators';
import { UserService } from '~services/user.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ChoiceDialogComponent } from '../ui-components/choice-dialog/choice-dialog.component';

@Component({
  selector: 'sa-box-pickup-sites',
  templateUrl: './box-pickup-sites.component.html',
  styleUrls: ['./box-pickup-sites.component.scss'],
})
export class BoxPickupSitesComponent implements OnInit {
  displayedColumns = ['name', 'oorMiles', 'rate', 'createdAt', 'directions'];
  dataSource: MatTableDataSource<BoxPickupSite> = new MatTableDataSource();
  public canManageBoxPickupSites$ = new BehaviorSubject<boolean>(false);

  constructor(
    private fracService: LmoFracsService,
    public userService: UserService,
    private matDialog: MatDialog,
    public snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    this.canManageBoxPickupSites$.next(this.userService.hasPermission('can_manage_box_pickup_sites'));
    if (this.canManageBoxPickupSites$.value) {
      if (!this.displayedColumns.includes('action')) {
        this.displayedColumns = [...this.displayedColumns, 'action'];
      }
    }

    this.loadBoxPickupSites();
  }

  private loadBoxPickupSites() {
    this.fracService.boxPickupSites$
      .pipe(
        map((data) => {
          data.sort((a, b) => {
            return a.name.localeCompare(b.name);
          });
          return data;
        }),
      )
      .subscribe(async (data) => {
        this.dataSource = new MatTableDataSource(data);
      });
  }

  public async promptAddNewDialog() {
    const dialogRef = this.matDialog.open(CreateBoxPickupSitesDialogComponent, {
      maxWidth: '90%',
      width: '70%',
      height: '90%',
    });
    const result = await dialogRef.afterClosed().toPromise();
    if (result) {
      this.loadBoxPickupSites();
    }
  }

  public async promptEditDialog(site: BoxPickupSite) {
    const dialogRef = this.matDialog.open(CreateBoxPickupSitesDialogComponent, {
      data: {
        id: site.id,
        siteId: site.siteId,
        name: site.name,
        outOfRouteMiles: site.outOfRouteMiles,
        outOfRouteRateCents: site.outOfRouteRateCents,
        lngLat: site.lngLat,
        radius: site.radius,
        createdAt: site.createdAt,
        directions: site.directions,
        hasActiveOrders: site.hasActiveOrders,
      },
      maxWidth: '90%',
      width: '70%',
      height: '90%',
    });

    const result = await dialogRef.afterClosed().toPromise();
    if (result) {
      this.loadBoxPickupSites();
    }
  }

  public async promptRemoveDialog(site: BoxPickupSite) {
    if (site.hasActiveOrders) {
      this.snackBar.open(`Cannot delete this site because it has active orders`, null, {
        duration: 2500,
        panelClass: ['snackbar-error'],
      });
      return;
    }

    const dialogRef = this.matDialog.open(ChoiceDialogComponent, {
      data: {
        button: ['Cancel', 'Remove'],
        context: `Are you sure you want to remove ${site.name}?`,
        desc: '',
      },
      maxWidth: '968px',
      width: '30%',
    });
    const result = await dialogRef.afterClosed().toPromise();
    if (result) {
      this.fracService.archiveBoxPickupSite$(site.id).subscribe(
        () => {
          this.snackBar.open(`Successfully remove box pickup site`, null, {
            duration: 2500,
          });
          this.loadBoxPickupSites();
        },
        () => {
          this.snackBar.open('Something went wrong', null, {
            duration: 2500,
            panelClass: ['snackbar-error'],
          });
        },
      );
    }
  }

  public sortTable(event) {
    switch (event.active) {
      case 'name':
        const sortedName = this.dataSource.data.sort((a, b) => {
          return a.name.localeCompare(b.name);
        });
        if (event.direction === 'desc') {
          sortedName.reverse();
        }
        this.dataSource.data = sortedName;
        break;
      case 'oorMiles':
        const sortedOORMiles = this.dataSource.data.sort((a, b) => {
          return a.outOfRouteMiles.toString().localeCompare(b.outOfRouteMiles.toString());
        });
        if (event.direction === 'desc') {
          sortedOORMiles.reverse();
        }
        this.dataSource.data = sortedOORMiles;
        break;
      case 'rate':
        const sortedRate = this.dataSource.data.sort((a, b) => {
          return a.outOfRouteRateCents - b.outOfRouteRateCents;
        });
        if (event.direction === 'desc') {
          sortedRate.reverse();
        }
        this.dataSource.data = sortedRate;
        break;
      case 'createdAt':
        const sortedCreatedAt = this.dataSource.data.sort((a, b) => {
          return a.createdAt.toString().localeCompare(b.createdAt.toString());
        });
        if (event.direction === 'desc') {
          sortedCreatedAt.reverse();
        }
        this.dataSource.data = sortedCreatedAt;
        break;
      case 'directions':
        const sortedDirections = this.dataSource.data.sort((a, b) => {
          return a.directions?.localeCompare(b.directions);
        });
        if (event.direction === 'desc') {
          sortedDirections.reverse();
        }
        this.dataSource.data = sortedDirections;
        break;
      default:
    }
  }
}
